import itemApi from "@/api/itemApi";
import store from "@/store";
import historyApi from "@/api/historyApi";
import tableApi from "@/api/tableApi";
import categoryApi from "@/api/categoryApi";
import awsApi from "@/api/awsApi";
import System from "@/model/system";
import { v4 as uuidv4 } from "uuid";
import mqtt, { MqttClient } from "mqtt";

const connectCheckInterval = 10 * 1000;
const functionInterval = 60 * 1000;
let connectCheckId = null;
let intervalFunctionId = null;

export class ReloadManager {
  private static instance: ReloadManager = undefined;
    private client?: MqttClient;
    protected system: System;
    private isStop = false;

    private constructor(system: System) {
      this.system = system;

      clearTimeout(connectCheckId);
      clearTimeout(intervalFunctionId);
      connectCheckId = setTimeout(this.connectCheck.bind(this), connectCheckInterval);
      intervalFunctionId = setTimeout(this.intervalFunction.bind(this), functionInterval);
    }

    private async intervalFunction() {
      if (this.isStop) {
        return;
      }
      if (store.getters['TableStore/isActiveAllYouCan']) {
        await this.getTables();
      }
      clearTimeout(intervalFunctionId);
      intervalFunctionId = setTimeout(this.intervalFunction.bind(this), functionInterval);
    }

    private async connectCheck() {
      if (this.isStop) {
        return;
      }
      if (!this.client || !this.client.connected) {
        await this.connectSocket(true);
      }
      clearTimeout(connectCheckId);
      connectCheckId = setTimeout(this.connectCheck.bind(this), connectCheckInterval);
    }

    public get connected() {
      return this.client?.connected;
    }
  
    public static load(system: System) {
      if (ReloadManager.instance == undefined) {
        ReloadManager.instance = new ReloadManager(system);
      }
  
      return ReloadManager.instance;
    }

    async connectSocket(forceReload = false) {
      const options = {
        keepalive: 30,
        clientId: uuidv4(),
        clean: true,
        reconnectPeriod: 0,
        connectTimeout: 30 * 1000,
      };
      if (this.client && this.client.connected) {
        return;
      }
      if (!this.system.shop_token || !this.system.session_token) {
        return;
      }
      // Call API get mqtt_endpoint
      const mqttEndpoint = await awsApi.getMqttEndpoint(this.system);
      this.client = mqtt.connect(mqttEndpoint, options);

      this.eventListener();
      if (forceReload) {
        await this.reloadAll();
      }
    }

    reloadAll() {
      return Promise.all([
        this.getItems(),
        this.getHistories(),
        this.getTables(),
      ]);
    }

    async getItems() {
      const context = await itemApi.getContext()
  
      // HACK: Should be a class to extract category data
      store.dispatch('CategoryStore/setCategoryToList', await categoryApi.getMainCategories(context));
      store.dispatch('CategoryStore/setSubCategoryToList', await categoryApi.getSubCategories(context));
  
      // HACK: Should be a class to extract item data, not an API
      const menuSetting = await itemApi.index(context)
      const items = menuSetting.menus;
      const settings = menuSetting.setting;
      const allRawItemList = menuSetting.allRawMenus;

      const setMenuList = await itemApi.getSetMenu(context)
      if (items !== null) {
        store.dispatch('MenuStore/clearList');
        store.dispatch('MenuStore/addMenuToList', items.reverse());
      }
      if (settings !== null) {
        store.dispatch('MenuStore/setMenuSetting', settings);
      }
      if (allRawItemList != null) {
        store.dispatch('MenuStore/addRawMenusToList', allRawItemList);
      }

      // update the option of menu list
      // HACK: Should be a class to extract item data, not an API
      const allMenu = await itemApi.getOptions(context)
      store.dispatch('OptionStore/resetOptionsToList', allMenu);
  
      if (items !== null ) {
        store.dispatch('MenuSetStore/clearList');
        items.map( async item => {
          const setMenus = await itemApi.getMenuSetListByIdentifier(item.identifier, setMenuList.setMenus, setMenuList.setMenuItemKeys, setMenuList.setMenuItems)
          setMenus.map(setMenu => {
            store.dispatch('MenuSetStore/addMenuSetToList', setMenu)
          })
        });
      }
    }
  
    async getHistories() {
      const histories = await historyApi.index()
      store.dispatch('HistoryStore/clear');
      histories.map(item => {
        store.dispatch('HistoryStore/addHistoryToList', item);
      })
    }
  
    async getTables() {
      const table = await tableApi.index();
      store.dispatch('TableStore/setTable', table);
    }
  
    public eventListener() {
      const broadcastTopic = `${this.system.topic_wc}broadcast`;
      const sessionTokenTopic = `${this.system.topic_wc}${this.system.session_token}`;
      this.client && this.client.on("connect", () => {
        console.log('[INFO] Connected');
  
        this.client.subscribe(broadcastTopic, (error) => {
          console.log(`[INFO] Subscribe ${broadcastTopic}`);
          if (error) {
            console.log(`[ERROR] Subscribe ${broadcastTopic}`)
            console.log(error);
          }
  
        });
  
        this.client.subscribe(sessionTokenTopic	, async (error) => {
          console.log(`[INFO] Subscribe ${sessionTokenTopic}`);
          if (error) {
            console.log(`[ERROR] Subscribe ${sessionTokenTopic}`)
            console.log(error);
          }
        });
      });
  
      this.client && this.client.on("message", async (mqttTopic, message) => {
        const json = JSON.parse(message.toString());
        if (!json.data) {
          return;
        }
  
        if (mqttTopic == broadcastTopic) {
          switch (json.data.content) {
          case 'shop-setting':
          case 'menu':
            await this.getItems();
            break;
          }
        }
  
        if (mqttTopic == sessionTokenTopic) {
          switch (json.data.content) {
          case 'table': await this.getTables(); break;
          case 'order': await this.getHistories(); break;
          case 'all-you-can': 
            await this.getTables();
            await this.getItems();
            break;
          }
        }
      });
  
      this.client && this.client.on("reconnect", async () => {
        if (!this.client.connected) {
          console.log('[INFO] Reconnect');
          // Connect socket again
          this.client.end();
          await this.connectSocket();
        }
      });
  
      this.client.on('error', async (error) => {
        console.log('[ERROR] Connection error: ' + error)
        this.client.end();
      })
  
      this.client.on('close', async () => {
        console.log('[INFO] disconnected');
      })
    }

    stop() {
      console.log('stop!!');
      this.isStop = true;
      clearTimeout(connectCheckId);
      clearTimeout(intervalFunctionId);
      this.client?.end();
    }
}
